import React, { useRef } from 'react';
import Cropper, { ReactCropperElement } from 'react-cropper';
import { Grid, Typography, Tooltip } from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import Modal from 'components/Modal';
import Button from 'components/Button';
import { Container, CropperBox } from './styles';
import 'cropperjs/dist/cropper.css';

interface IProps {
  open: boolean;
  aspectRatio: number;
  file?: File;
  onCancel: () => void;
  onConfirm: (file: File) => void;
}

const zoomButtonStyle: React.CSSProperties = {
  minWidth: '36px',
  width: '36px',
  height: '36px',
  padding: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '8px',
  fontSize: '18px',
  fontWeight: 'bold',
  lineHeight: 1,
};

const ImageAdjustModal = ({ open, aspectRatio, file, onCancel, onConfirm }: IProps): JSX.Element => {
  if (!file) return <></>;

  const cropperRef = useRef<ReactCropperElement>(null);
  const imageUrl = React.useMemo(() => (file ? URL.createObjectURL(file) : ''), [file]);

  const handleConfirm = () => {
    const cropper = cropperRef.current?.cropper;
    if (!cropper) {
      onConfirm(file);
      return;
    }

    const data = cropper.getData(true);
    const imageData = cropper.getImageData();
    const naturalWidth = Math.round(imageData.naturalWidth);
    const naturalHeight = Math.round(imageData.naturalHeight);

    const cropHash = `crop=${Math.round(data.x)},${Math.round(data.y)},${Math.round(data.width)},${Math.round(
      data.height
    )},${naturalWidth},${naturalHeight}`;
    (file as any).cropHash = cropHash;

    onConfirm(file);
  };

  const handleZoomIn = () => {
    cropperRef.current?.cropper.zoom(0.1);
  };

  const handleZoomOut = () => {
    cropperRef.current?.cropper.zoom(-0.1);
  };

  const handleReset = () => {
    cropperRef.current?.cropper.reset();
  };

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    const cropper = cropperRef.current?.cropper;
    if (!cropper) return;
    const ratio = e.deltaY < 0 ? 0.08 : -0.08;
    cropper.zoom(ratio);
  };

  const handleZoom = (e: any) => {
    const cropper = cropperRef.current?.cropper;
    if (!cropper) return;

    // Apenas restringe o zoom out
    if (e.detail.ratio < e.detail.oldRatio) {
      const canvasData = cropper.getCanvasData();
      const cropBoxData = cropper.getCropBoxData();
      const newWidth = canvasData.naturalWidth * e.detail.ratio;
      const newHeight = canvasData.naturalHeight * e.detail.ratio;

      // Bloqueia zoom out se o canvas ficaria menor que o cropBox
      if (newWidth < cropBoxData.width || newHeight < cropBoxData.height) {
        e.preventDefault();
      }
    }
  };

  return (
    <Modal open={open} onClose={onCancel}>
      <Container>
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <Typography variant="body1" align="center" color="textSecondary">
              Ajuste a área da foto: use o scroll para zoom e arraste a imagem para reposicioná-la
            </Typography>
          </Grid>
          <Grid item xs>
            <CropperBox
              onWheel={handleWheel}
              style={{ '--cropper-bg-image': `url("${imageUrl}")` } as React.CSSProperties}>
              <Cropper
                src={imageUrl}
                aspectRatio={aspectRatio}
                viewMode={0}
                zoomable={true}
                cropBoxResizable={false}
                cropBoxMovable={false}
                dragMode="move"
                zoomOnWheel={false}
                scalable={false}
                autoCropArea={1}
                background={false}
                highlight={false}
                center={true}
                ref={cropperRef}
              />
            </CropperBox>
          </Grid>
          <Grid item container justifyContent="center" spacing={1} style={{ marginTop: '10px' }}>
            <Grid item>
              <Tooltip title="Diminuir zoom">
                <span>
                  <Button
                    buttonType="secondary"
                    onClick={handleZoomOut}
                    aria-label="Diminuir zoom"
                    style={zoomButtonStyle}>
                    -
                  </Button>
                </span>
              </Tooltip>
            </Grid>
            <Grid item>
              <Tooltip title="Redefinir imagem">
                <span>
                  <Button
                    buttonType="secondary"
                    onClick={handleReset}
                    aria-label="Redefinir imagem"
                    style={zoomButtonStyle}>
                    <RestartAltIcon style={{ fontSize: 20 }} />
                  </Button>
                </span>
              </Tooltip>
            </Grid>
            <Grid item>
              <Tooltip title="Aumentar zoom">
                <span>
                  <Button
                    buttonType="secondary"
                    onClick={handleZoomIn}
                    aria-label="Aumentar zoom"
                    style={zoomButtonStyle}>
                    +
                  </Button>
                </span>
              </Tooltip>
            </Grid>
          </Grid>
          <Grid item container justifyContent="center">
            <Button buttonType="primary" onClick={handleConfirm}>
              Confirmar
            </Button>
          </Grid>
        </Grid>
      </Container>
    </Modal>
  );
};

export default ImageAdjustModal;
